import React from 'react';

import { noop } from 'domain/utils';

interface TErrorBoundaryContext {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  clearError: VoidFunction;
}

export const ErrorBoundaryContext = React.createContext<TErrorBoundaryContext>({
  clearError: noop,
});

/**
 * Provides access to the error. Can be used in the ErrorBoundary `fallback` component
 * @returns
 */
export const useError = (): TErrorBoundaryContext => {
  return React.useContext(ErrorBoundaryContext);
};

interface ErrorBoundaryProps {
  fallback: React.ReactNode;
  onError?(error: Error, errorInfo: React.ErrorInfo): void;
}
/**
 * Component rendered when an error occured in any children.
 * Allows clearing the error to retry rendering the content.
 * Provides access to the error
 */
export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<ErrorBoundaryProps>
> {
  state = { hasError: false, error: undefined, errorInfo: undefined };

  constructor(props: any) {
    super(props);

    this.clearError = this.clearError.bind(this);
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    this.setState({
      hasError: true,
      error,
      errorInfo,
    });
  }

  clearError() {
    this.setState({
      error: undefined,
      hasError: false,
      errorInfo: undefined,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryContext.Provider
          value={{ ...this.state, clearError: this.clearError }}
        >
          {this.props.fallback}
        </ErrorBoundaryContext.Provider>
      );
    }
    return this.props.children;
  }
}
